import React, { useState } from "react";
import { Flex, Text } from "@chakra-ui/react";
import CustomIcon from "../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import MetricRow from "@/features/pathpick/components/product/PathPickProductCardMetricRow";
import PathPickPoizonSellerCenterModal from "@/features/pathpick/components/product/PathPickPoizonSellerCenterModal";
import {
  PathPickMarketPriceBasis,
  PathPickProductCardComparisonLayout,
} from "@/features/pathpick/types";
import {
  PathPickMarketMetrics,
  PATH_PICK_MARKET_BASIS_LABEL_KEY,
  PATH_PICK_MARKET_LABEL,
  PATH_PICK_MARKET_SALES_LABEL_KEY,
} from "@/features/pathpick/utils/market";

interface Props {
  metrics: PathPickMarketMetrics;
  basis: PathPickMarketPriceBasis;
  layout: PathPickProductCardComparisonLayout;
  // 미매칭 마켓은 null — 그때는 ↗ 아이콘도, 클릭도 없다(열 상품이 없다).
  externalUrl: string | null;
  onOpenMarginFormula: () => void;
}

/**
 * 카드 안 마켓 1개 블록 — `KREAM ↗` 헤더 + 기준가 / 거래수 / 예상 마진.
 *
 * 좌우(나란히) 배치는 열이 좁아 값을 **항상** 라벨 아래 줄 우측에 둔다 — 행마다 한 줄/두 줄이
 * 섞이면 두 마켓 열의 행 높이가 어긋나 나란히 비교되지 않는다. 비교 블록이 화면 폭과
 * 무관하게 좌우로 놓이므로 이 배치도 모바일에서 그대로 쓴다.
 *
 * 최저 입찰가 값을 눌러 열던 마켓 링크는 이 헤더로 옮겨 왔다 — 가격 값은 더 이상 링크가 아니다.
 */
function PathPickProductCardMarketBlock({
  metrics,
  basis,
  layout,
  externalUrl,
  onOpenMarginFormula,
}: Props) {
  const { t, tNumberString, tCurrencyString } = useI18nHelper();
  const marketLabel = PATH_PICK_MARKET_LABEL[metrics.market];
  const metricDirection =
    layout === "side" ? "column" : "row";

  const formatKrw = (value?: number | null) =>
    value != null
      ? tCurrencyString(value, { type: "SYMBOL", code: "KRW" })
      : undefined;

  // 포이즌은 클릭 즉시 열지 않고 안내 모달을 한 번 거친다(아래 handleOpenExternal 주석).
  const [isSellerCenterOpen, setIsSellerCenterOpen] = useState(false);

  const handleOpenExternal = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!externalUrl) {
      return;
    }
    // 포이즌 주소는 공개 상품 페이지가 아니라 판매자 로그인이 필요한 셀러센터라,
    // 어디로 가는지 알린 뒤 사용자가 이동 버튼을 눌렀을 때 연다.
    if (metrics.market === "POIZON") {
      setIsSellerCenterOpen(true);
      return;
    }
    window.open(externalUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <Flex flexDirection={"column"} gap={"4px"} flex={1} minWidth={0}>
        <Flex
          gap={"4px"}
          alignItems={"center"}
          width={"fit-content"}
          color={"#444440"}
          {...(externalUrl && {
            cursor: "pointer",
            _hover: { color: "#1272EF" },
            onClick: handleOpenExternal,
          })}
        >
          <Text textStyle={"body-sm-bold"} fontSize={"11px"}>
            {marketLabel}
          </Text>
          {externalUrl && (
            <CustomIcon src={"external-link"} width={14} height={14} />
          )}
        </Flex>

        {/* 기준가 — 미매칭 마켓은 "{마켓} 미매칭"을 주황으로 대신 보여준다.
          매칭됐는데 고른 기준의 시세만 없으면 "-"로 둔다(매칭 여부를 단정하지 않는다). */}
        <MetricRow
          direction={metricDirection}
          label={t(PATH_PICK_MARKET_BASIS_LABEL_KEY[metrics.market][basis])}
          value={formatKrw(metrics.basisValue)}
          valueStyle={{ color: "#1272EF" }}
          emptyText={
            metrics.isMatched
              ? undefined
              : t("path_pick:market.unmatched", { market: marketLabel })
          }
          emptyValueStyle={metrics.isMatched ? undefined : { color: "#B27700" }}
        />

        <MetricRow
          direction={metricDirection}
          label={t(PATH_PICK_MARKET_SALES_LABEL_KEY[metrics.market])}
          value={
            metrics.salesCount != null
              ? t("path_pick:search_products.popular.bid_count_value", {
                  value: tNumberString(metrics.salesCount),
                })
              : undefined
          }
        />

        <MetricRow
          direction={metricDirection}
          label={t("path_pick:search_products.product_list.expected_margin")}
          value={formatKrw(metrics.expectedMargin)}
          sub={
            metrics.marginRate != null
              ? `${metrics.marginRate.toFixed(1)}%`
              : undefined
          }
          valueStyle={{
            color:
              metrics.expectedMargin != null && metrics.expectedMargin < 0
                ? "#CC1D1D"
                : "#16B41C",
          }}
          emptyText={t(
            "path_pick:search_products.product_list.card.margin_calc_unavailable"
          )}
          emptyValueStyle={{ color: "#8F8F8C" }}
          onClickValue={onOpenMarginFormula}
          onInfoClick={onOpenMarginFormula}
        />
      </Flex>

      {/* Portal 로 나가므로 카드 안에 있어도 카드 클릭(상품 선택 토글)에 영향이 없다. */}
      <PathPickPoizonSellerCenterModal
        isOpen={isSellerCenterOpen}
        onClose={() => setIsSellerCenterOpen(false)}
        url={externalUrl}
      />
    </>
  );
}

export default PathPickProductCardMarketBlock;
